import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder } from '@angular/forms';
import { Auth } from '@freelancer/auth';
import {
  BackendPushErrorResponse,
  Datastore,
  DatastoreCollection,
  startWithEmptyList,
} from '@freelancer/datastore';
import {
  BaseUser,
  BillingAgreement,
  BillingAgreementsCollection,
  CurrenciesIncludingExternalCollection,
  Currency,
  PaymentShareMember,
  PaymentShareMembersCollection,
  PaymentShareMembersSpentAmountCollection,
  PaymentShareTeam,
  PaymentShareTeamsCollection,
  UsersCollection,
} from '@freelancer/datastore/collections';
import { PaymentsUtils } from '@freelancer/payments-utils';
import { ModalRef } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { PictureImage } from '@freelancer/ui/picture';
import { FontType, FontWeight, TextSize } from '@freelancer/ui/text';
import { ToggleSize } from '@freelancer/ui/toggle';
import {
  dirtyAndValidate,
  maxLength,
  minValue,
  required,
} from '@freelancer/ui/validators';
import { isDefined, isFormControl, toNumber } from '@freelancer/utils';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import { DepositMethodApi } from 'api-typings/payments/payments';
import {
  PaymentShareEntityTypeApi,
  PaymentShareMemberStatusApi,
  PaymentShareSpendingLimitDurationApi,
  PaymentShareTeamStatusApi,
} from 'api-typings/users/users';
import * as Rx from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { PaymentShareMemberInfo } from './payment-share-modal.types';

export enum FormField {
  IS_TEAM_PAYMENT_ENABLED = 'isTeamPaymentEnabled',
  PAYMENT_METHOD_NAME = 'paymentMethodName',
  IS_SPENDING_LIMIT_ACTIVE = 'isSpendingLimitActive',
  SPENDING_LIMIT_DURATION = 'spendingLimitDuration',
  MAXIMUM_SPEND_LIMIT = 'maximumSpendLimit',
}

enum ModalResult {
  SETUP = 'setup',
  UPDATE = 'update',
}

@Component({
  template: `
    <fl-bit
      *ngIf="(isLoading$ | flAsync) === false; else loading"
      flTrackingSection="PaymentShareModal"
    >
      <fl-banner-alert
        *ngIf="errorMessage$ | flAsync as error"
        [type]="BannerAlertType.ERROR"
        [flMarginBottom]="Margin.SMALL"
        (close)="onErrorBannerClose()"
      >
        <fl-generic-error i18n="Error Message" [error]="error">
          Failed to create or update the payment share team.
        </fl-generic-error>
      </fl-banner-alert>

      <fl-bit [flMarginBottom]="Margin.MID">
        <fl-heading
          *ngIf="!isTeamExists"
          i18n="Set up Team Payment Setting Modal Header"
          [flMarginBottom]="Margin.XXXSMALL"
          [headingType]="HeadingType.H2"
          [size]="TextSize.MID"
          >Share payment method
        </fl-heading>
        <fl-heading
          *ngIf="isTeamExists"
          i18n="Team Payment Optional Modal Header"
          [flMarginBottom]="Margin.XXXSMALL"
          [headingType]="HeadingType.H2"
          [size]="TextSize.MID"
          >Payment sharing settings
        </fl-heading>
        <fl-text i18n="Team Payment Setting Modal Description">
          Enabling payment sharing allows team members to access your payment
          method. Team members will not have access to any of the underlying
          payment method details.
        </fl-text>
      </fl-bit>

      <fl-bit
        *ngIf="formGroup.get(FormField.IS_TEAM_PAYMENT_ENABLED) as control"
        class="TeamPaymentToggle"
        [flMarginBottom]="Margin.MID"
      >
        <fl-toggle
          *ngIf="isFormControl(control)"
          flTrackingLabel="TeamPaymentToggle"
          [control]="control"
          [size]="ToggleSize.MID"
        >
        </fl-toggle>
        <fl-text
          class="ToggleLabel"
          i18n="Team Payment Enabled"
          [weight]="FontWeight.BOLD"
        >
          Enable payment sharing
        </fl-text>
      </fl-bit>

      <app-payment-share-modal-credit-card
        *ngIf="billingAgreement$ | flAsync as billingAgreement"
        [billingAgreement]="billingAgreement"
        [billingAgreementCurrency]="currency$ | flAsync"
        [logo]="logo$ | flAsync"
        [flMarginRight]="Margin.MID"
      ></app-payment-share-modal-credit-card>

      <fl-bit [flMarginBottom]="Margin.MID">
        <app-payment-share-modal-general-setting
          [formGroup]="formGroup"
          [billingAgreementCurrency]="currency$ | flAsync"
        >
        </app-payment-share-modal-general-setting>
      </fl-bit>
      <fl-bit>
        <!-- MembersSettings Component-->
      </fl-bit>
      <fl-bit>
        <app-payment-share-modal-members
          [members]="memberInfos$ | flAsync"
          [ownerUserId]="userId$ | flAsync"
          [currency]="currency$ | flAsync"
          [usernameFormArray]="usernameFormArray"
          [isTeamPaymentEnabled]="
            formGroup.get(FormField.IS_TEAM_PAYMENT_ENABLED)?.value
          "
          [showBalanceLeft]="showBalanceLeft$ | flAsync"
          (userChecked)="updateValidatedUsers($event)"
          (usersToRemove)="updateRemovedUsers($event)"
        ></app-payment-share-modal-members>
      </fl-bit>
      <fl-bit class="ButtonContainer">
        <fl-bit class="ButtonContainer-right">
          <fl-button
            flTrackingLabel="CancelButton"
            i18n="Team Payment Setting Modal Cancel Button"
            [color]="ButtonColor.TRANSPARENT_DARK"
            [flMarginRight]="Margin.XSMALL"
            [size]="ButtonSize.SMALL"
            (click)="handleCancel()"
          >
            Cancel
          </fl-button>
          <fl-button
            flTrackingLabel="SaveButton"
            i18n="Team Payment Setting Modal Save Button"
            [color]="ButtonColor.SECONDARY"
            [size]="ButtonSize.SMALL"
            [busy]="isSaving$ | flAsync"
            [disabled]="!formGroup.valid || !usernameFormArray.valid"
            (click)="handleSave()"
          >
            Save
          </fl-button>
        </fl-bit>
      </fl-bit>
    </fl-bit>
    <ng-template #loading>
      <fl-spinner
        flTrackingLabel="PaymentShareModalSpinner"
        [overlay]="true"
      ></fl-spinner>
    </ng-template>
  `,
  styleUrls: ['./payment-share-modal.component.scss'],
})
export class PaymentShareModalComponent implements OnInit, OnDestroy {
  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  ErrorCodeApi = ErrorCodeApi;
  FontType = FontType;
  FontWeight = FontWeight;
  HeadingType = HeadingType;
  isFormControl = isFormControl;
  Margin = Margin;
  TextSize = TextSize;
  ToggleSize = ToggleSize;
  FormField = FormField;

  @Input() billingAgreementId: number;
  // Determine whether the billing agreement has a payment share team
  @Input() isTeamExists: boolean;

  billingAgreement$: Rx.Observable<BillingAgreement>;
  currency$: Rx.Observable<Currency | undefined>;
  logo$: Rx.Observable<PictureImage | undefined>;
  paymentShareTeam$: Rx.Observable<PaymentShareTeam | undefined>;
  paymentShareTeamsCollection: DatastoreCollection<PaymentShareTeamsCollection>;
  userId$: Rx.Observable<number>;
  showBalanceLeft$: Rx.Observable<boolean>;

  paymentShareMembersCollection: DatastoreCollection<PaymentShareMembersCollection>;
  memberInfos$: Rx.Observable<readonly PaymentShareMemberInfo[]>;
  paymentShareMembers$: Rx.Observable<readonly PaymentShareMember[]>;
  usernameFormArray = new FormArray([]); // only support usernames until we support invitations

  // this is to store the results of the async validation requests made to the users api, which we will need when we add the users as members to the team
  validatedUsers: readonly BaseUser[] = [];

  private membersToRemoveSubject$ = new Rx.BehaviorSubject<
    readonly PaymentShareMember[]
  >([]);
  membersToRemove$ = this.membersToRemoveSubject$.asObservable();

  private errorMessageSubject$ = new Rx.BehaviorSubject<
    BackendPushErrorResponse<PaymentShareTeamsCollection> | undefined
  >(undefined);
  errorMessage$ = this.errorMessageSubject$.asObservable();

  private isLoadingSubject$ = new Rx.BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject$.asObservable();

  private isSavingSubject$ = new Rx.BehaviorSubject<boolean>(false);
  isSaving$ = this.isSavingSubject$.asObservable();

  subscriptions = new Rx.Subscription();

  formGroup = this.fb.group({
    [FormField.IS_TEAM_PAYMENT_ENABLED]: [false],
    [FormField.PAYMENT_METHOD_NAME]: [
      { value: '', disabled: true },
      [
        required($localize`Please enter the payment method name`),
        maxLength(32, $localize`Please enter at most 32 characters.`),
      ],
    ],
    [FormField.IS_SPENDING_LIMIT_ACTIVE]: [{ value: false, disabled: true }],
    [FormField.SPENDING_LIMIT_DURATION]: [
      { value: PaymentShareSpendingLimitDurationApi.PER_MONTH, disabled: true },
    ],
    [FormField.MAXIMUM_SPEND_LIMIT]: {
      value: null,
      disabled: true,
    },
  });

  constructor(
    private auth: Auth,
    private fb: FormBuilder,
    private datastore: Datastore,
    private paymentsUtils: PaymentsUtils,
    private modalRef: ModalRef<PaymentShareModalComponent>,
  ) {}

  ngOnInit() {
    // If there is an existing team set isLoading to true
    this.isLoadingSubject$.next(this.isTeamExists);

    this.userId$ = this.auth.getUserId().pipe(map(toNumber), filter(isDefined));

    this.billingAgreement$ = this.datastore
      .document<BillingAgreementsCollection>(
        'billingAgreements',
        this.billingAgreementId,
      )
      .valueChanges();

    const currencies$ = this.datastore
      .collection<CurrenciesIncludingExternalCollection>(
        'currenciesIncludingExternal',
      )
      .valueChanges();

    this.currency$ = Rx.combineLatest([
      this.billingAgreement$,
      currencies$,
    ]).pipe(
      map(([billingAgreement, currencies]) =>
        currencies.find(c => c.id === billingAgreement.currencyId),
      ),
    );

    this.paymentShareTeamsCollection = this.datastore.collection<PaymentShareTeamsCollection>(
      'paymentShareTeams',
      query => query.where('billingAgreementId', '==', this.billingAgreementId),
    );

    this.paymentShareTeam$ = this.paymentShareTeamsCollection
      .valueChanges()
      .pipe(
        map(paymentTeams =>
          paymentTeams.length > 0 ? paymentTeams[0] : undefined,
        ),
      );

    this.paymentShareMembersCollection = this.datastore.collection<PaymentShareMembersCollection>(
      'paymentShareMembers',
      query =>
        this.paymentShareTeam$.pipe(
          map(team =>
            team
              ? query
                  .where('paymentShareTeamId', '==', team.id)
                  .where('status', '==', PaymentShareMemberStatusApi.ADDED)
              : query.null(),
          ),
        ),
    );

    this.paymentShareMembers$ = this.paymentShareMembersCollection.valueChanges();

    const paymentShareMemberUserIds$ = this.paymentShareMembers$.pipe(
      map(members =>
        members
          .filter(
            member => member.entityType === PaymentShareEntityTypeApi.USER,
          )
          .map(member => member.entityId),
      ),
    );

    const paymentShareMemberIds$ = this.paymentShareMembers$.pipe(
      map(members => members.map(member => member.id)),
    );

    const paymentShareMembersSpentAmount$ = this.datastore
      .collection<PaymentShareMembersSpentAmountCollection>(
        'paymentShareMembersSpentAmount',
        query => query.where('id', 'in', paymentShareMemberIds$),
      )
      .valueChanges()
      .pipe(startWithEmptyList());

    const memberUsers$ = this.datastore
      .collection<UsersCollection>(
        'users',
        Rx.iif(
          () => this.isTeamExists,
          paymentShareMemberUserIds$,
          this.userId$.pipe(
            take(1),
            map(user => [user]),
          ),
        ),
      )
      .valueChanges();

    this.memberInfos$ = Rx.combineLatest([
      memberUsers$,
      this.paymentShareTeam$,
      paymentShareMembersSpentAmount$,
    ]).pipe(
      map(([users, team, membersSpentAmount]) =>
        users.map(user => {
          if (team) {
            const spentAmount = membersSpentAmount.find(
              member =>
                member.entityId === user.id &&
                member.entityType === PaymentShareEntityTypeApi.USER,
            )?.currentSpentAmount;
            const memberIsTeamOwner =
              user.id === team.ownerEntityId &&
              team.ownerEntityType === PaymentShareEntityTypeApi.USER;
            const isSpendingLimitActive = memberIsTeamOwner
              ? false
              : team.isSpendingLimitActive;
            const spendingLimitAmount = team.memberSpendingLimitAmount;
            return {
              user,
              isSpendingLimitActive,
              spendingLimitAmount,
              spentAmount,
            };
          }
          return { user };
        }),
      ),
    );

    this.showBalanceLeft$ = this.paymentShareTeam$.pipe(
      map(team => team?.isSpendingLimitActive !== false),
    );

    this.logo$ = this.billingAgreement$.pipe(
      map(billingAgreement => {
        switch (billingAgreement.depositMethod) {
          case DepositMethodApi.PAYPAL_REFERENCE:
            return {
              src: this.paymentsUtils.getMethodLogoPath(
                DepositMethodApi.PAYPAL,
              ),
              alt: 'Paypal logo',
            };
          case DepositMethodApi.ENTERPRISE_BILLING:
            return {
              src: this.paymentsUtils.getMethodLogoPath(
                DepositMethodApi.ENTERPRISE_BILLING,
              ),
              alt: 'Enterprise billing agreement logo',
            };
          case DepositMethodApi.FLN_BILLING:
            return billingAgreement.creditCard &&
              billingAgreement.creditCard.cardType
              ? {
                  src: this.paymentsUtils.getMethodLogoPath(
                    billingAgreement.creditCard.cardType,
                  ),
                  alt: `${
                    billingAgreement.creditCard.cardType || 'credit card'
                  } logo`,
                }
              : {
                  src: this.paymentsUtils.getMethodLogoPath(
                    billingAgreement.depositMethod,
                  ),
                  alt: 'credit card logo',
                };
          default:
            // Use FLN_BILLING Image as default
            return {
              src: this.paymentsUtils.getMethodLogoPath(
                DepositMethodApi.FLN_BILLING,
              ),
              alt: 'Payment Method logo',
            };
        }
      }),
    );

    this.subscriptions.add(
      this.formGroup.controls[
        FormField.IS_TEAM_PAYMENT_ENABLED
      ].valueChanges.subscribe(isTeamPaymentEnabled => {
        if (!isTeamPaymentEnabled) {
          this.formGroup.controls[FormField.PAYMENT_METHOD_NAME].disable();
          this.formGroup.controls[FormField.IS_SPENDING_LIMIT_ACTIVE].disable();
          this.formGroup.controls[FormField.SPENDING_LIMIT_DURATION].disable();
          this.formGroup.controls[FormField.MAXIMUM_SPEND_LIMIT].disable();
        } else {
          this.formGroup.controls[FormField.PAYMENT_METHOD_NAME].enable();
          this.formGroup.controls[FormField.IS_SPENDING_LIMIT_ACTIVE].enable();
          this.formGroup.controls[FormField.SPENDING_LIMIT_DURATION].enable();
          this.formGroup.controls[FormField.MAXIMUM_SPEND_LIMIT].enable();
        }
      }),
    );

    this.subscriptions.add(
      this.formGroup.controls[
        FormField.IS_SPENDING_LIMIT_ACTIVE
      ].valueChanges.subscribe(isSpendingLimitActive => {
        this.formGroup.controls[FormField.MAXIMUM_SPEND_LIMIT].setValidators(
          isSpendingLimitActive
            ? [
                required('Please enter a spend limit.'),
                minValue(0, 'Please enter at least 0.'),
              ]
            : null,
        );
        this.formGroup.controls[
          FormField.MAXIMUM_SPEND_LIMIT
        ].updateValueAndValidity();
      }),
    );

    this.subscriptions.add(
      this.paymentShareTeam$.subscribe(paymentShareTeam => {
        if (paymentShareTeam) {
          this.setFormGroupValueFromPaymentTeam(paymentShareTeam);
          // Set isLoading to false after update the form group value
          this.isLoadingSubject$.next(false);
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  handleCancel() {
    this.modalRef.close();
  }

  handleSave() {
    dirtyAndValidate(this.formGroup);
    if (!this.formGroup.valid || !this.usernameFormArray.valid) {
      return;
    }

    Rx.from(
      this.isTeamExists
        ? this.updatePaymentShareTeam()
        : this.createPaymentShareTeam(),
    )
      .pipe(take(1))
      .toPromise()
      .then(teamId => {
        if (!teamId) {
          return true;
        }

        return (this.usernameFormArray.value as string[])
          .map(value =>
            this.validatedUsers.find(
              user => user.username.toUpperCase() === value.toUpperCase(),
            ),
          )
          .filter(isDefined)
          .map(user => {
            this.paymentShareMembersCollection
              .push({
                paymentShareTeamId: teamId,
                entityId: user.id,
                entityType: PaymentShareEntityTypeApi.USER,
                status: PaymentShareMemberStatusApi.ADDED,
                useTeamSpendLimit: true,
                isSpendingLimitActive: true,
                spendingLimitDuration:
                  PaymentShareSpendingLimitDurationApi.NO_TIME_LIMIT,
                spendingLimitAmount: 0,
              })
              .then(response => {
                if (response.status !== 'success') {
                  this.errorMessageSubject$.next(response);
                  return true;
                }
              });
            return false;
          })
          .reduce((acc, i) => acc || i, false);
      })
      .then(error => {
        if (!error) {
          this.modalRef.close(
            this.isTeamExists ? ModalResult.UPDATE : ModalResult.SETUP,
          );
        }
      });
  }

  async createPaymentShareTeam(): Promise<number | undefined> {
    const form = this.formGroup.value;
    if (!form[FormField.IS_TEAM_PAYMENT_ENABLED]) {
      // Close the modal if team payment is disabled
      this.modalRef.close();
      return;
    }
    this.isSavingSubject$.next(true);

    return Rx.combineLatest([this.billingAgreement$, this.userId$])
      .pipe(take(1))
      .toPromise()
      .then(([billingAgreement, userId]) =>
        this.datastore.createDocument<PaymentShareTeamsCollection>(
          'paymentShareTeams',
          {
            ownerEntityId: userId,
            ownerEntityType: PaymentShareEntityTypeApi.USER,
            paymentShareTeamName: form[FormField.PAYMENT_METHOD_NAME],
            status: form[FormField.IS_TEAM_PAYMENT_ENABLED]
              ? PaymentShareTeamStatusApi.ENABLED
              : PaymentShareTeamStatusApi.DISABLED,
            memberSpendingLimitDuration:
              form[FormField.SPENDING_LIMIT_DURATION],
            memberSpendingLimitAmount: form[FormField.IS_SPENDING_LIMIT_ACTIVE]
              ? toNumber(form[FormField.MAXIMUM_SPEND_LIMIT])
              : 0,
            isSpendingLimitActive: form[FormField.IS_SPENDING_LIMIT_ACTIVE],
            billingAgreementId: this.billingAgreementId,
            depositMethod: billingAgreement.depositMethod,
            isTeamOwner: true,
          },
        ),
      )
      .then(response => {
        this.isSavingSubject$.next(false);
        if (response.status !== 'success') {
          this.errorMessageSubject$.next(response);
        } else {
          return response.id;
        }
      });
  }

  async updatePaymentShareTeam(): Promise<number | undefined> {
    const form = this.formGroup.value;
    this.isSavingSubject$.next(true);

    // Remove team members
    // TODO: Add error handling
    this.membersToRemove$
      .pipe(take(1))
      .toPromise()
      .then(membersToRemove => {
        membersToRemove.map(member => {
          this.paymentShareMembersCollection.update(member.id, {
            paymentShareTeamId: member.paymentShareTeamId,
            entityId: member.id,
            entityType: member.entityType,
            status: PaymentShareMemberStatusApi.REMOVED,
            useTeamSpendLimit: true,
            isSpendingLimitActive: true,
            spendingLimitDuration:
              PaymentShareSpendingLimitDurationApi.NO_TIME_LIMIT,
            spendingLimitAmount: 0,
          });
        });
      });

    let teamId: number;

    return Rx.combineLatest([
      this.billingAgreement$,
      this.userId$,
      this.paymentShareTeam$,
    ])
      .pipe(take(1))
      .toPromise()
      .then(([billingAgreement, userId, paymentShareTeam]) => {
        if (!paymentShareTeam) {
          return undefined;
        }

        teamId = paymentShareTeam.id;

        return this.paymentShareTeamsCollection.update(paymentShareTeam.id, {
          id: paymentShareTeam.id,
          ownerEntityId: userId,
          ownerEntityType: PaymentShareEntityTypeApi.USER,
          paymentShareTeamName: form[FormField.PAYMENT_METHOD_NAME],
          status: form[FormField.IS_TEAM_PAYMENT_ENABLED]
            ? PaymentShareTeamStatusApi.ENABLED
            : PaymentShareTeamStatusApi.DISABLED,
          memberSpendingLimitDuration: form[FormField.SPENDING_LIMIT_DURATION],
          memberSpendingLimitAmount: form[FormField.IS_SPENDING_LIMIT_ACTIVE]
            ? toNumber(form[FormField.MAXIMUM_SPEND_LIMIT])
            : 0,
          isSpendingLimitActive: form[FormField.IS_SPENDING_LIMIT_ACTIVE],
          billingAgreementId: this.billingAgreementId,
          depositMethod: billingAgreement.depositMethod,
          isTeamOwner: true,
        });
      })
      .then(response => {
        this.isSavingSubject$.next(false);
        if (!response) {
          this.errorMessageSubject$.next({
            status: 'error',
            errorCode: 'UNKNOWN_ERROR',
          });
        } else if (response.status !== 'success') {
          this.errorMessageSubject$.next(response);
        } else {
          return teamId;
        }
      });
  }

  setFormGroupValueFromPaymentTeam(team: PaymentShareTeam) {
    this.formGroup.patchValue(
      {
        [FormField.IS_TEAM_PAYMENT_ENABLED]:
          team.status === PaymentShareTeamStatusApi.ENABLED,
        [FormField.PAYMENT_METHOD_NAME]: team.paymentShareTeamName,
        [FormField.IS_SPENDING_LIMIT_ACTIVE]: team.isSpendingLimitActive,
        [FormField.SPENDING_LIMIT_DURATION]: team.memberSpendingLimitDuration,
        [FormField.MAXIMUM_SPEND_LIMIT]: team.memberSpendingLimitAmount,
      },
      {
        emitEvent: true,
      },
    );
  }

  onErrorBannerClose() {
    this.errorMessageSubject$.next(undefined);
  }

  updateValidatedUsers(user: BaseUser) {
    if (
      !this.validatedUsers.some(validatedUser => validatedUser.id === user.id)
    ) {
      this.validatedUsers = [...this.validatedUsers, user];
    }
  }

  updateRemovedUsers(userIds: readonly number[]) {
    this.paymentShareMembers$
      .pipe(take(1))
      .toPromise()
      .then(paymentShareMembers => {
        const removedMembers = paymentShareMembers.filter(member =>
          userIds.includes(member.entityId),
        );
        this.membersToRemoveSubject$.next(removedMembers);
      });
  }
}
